import type { LayoutItem, WidgetDef } from './types';
import RevenueStatsWidget from './RevenueStatsWidget';
import UpcomingBookingsWidget from './UpcomingBookingsWidget';
import RevenueMtdWidget from './RevenueMtdWidget';
import RentalsMtdWidget from './RentalsMtdWidget';
import ActiveVehiclesWidget from './ActiveVehiclesWidget';
import UtilizationWidget from './UtilizationWidget';
import CarYearsWidget from './CarYearsWidget';
import UpcomingReturnsWidget from './UpcomingReturnsWidget';
import OngoingRentalsWidget from './OngoingRentalsWidget';
import UpcomingServiceWidget from './UpcomingServiceWidget';

export const WIDGET_REGISTRY: WidgetDef[] = [
  // KPI-kort — col_span 1, vises på 4 kolonner i toppen
  {
    key: 'kpi_revenue_mtd',
    titleKey: 'dashboard.kpi_revenue_mtd',
    descriptionKey: 'dashboard.kpi_revenue_mtd_desc',
    component: RevenueMtdWidget,
    minColSpan: 1, maxColSpan: 1,
    supportsHeightChoice: false,
    defaultColSpan: 1, defaultHeight: 'auto',
    defaultVisibleWeb: true, defaultVisibleMobile: true,
  },
  {
    key: 'kpi_rentals_mtd',
    titleKey: 'dashboard.kpi_rentals_mtd',
    descriptionKey: 'dashboard.kpi_rentals_mtd_desc',
    component: RentalsMtdWidget,
    minColSpan: 1, maxColSpan: 1,
    supportsHeightChoice: false,
    defaultColSpan: 1, defaultHeight: 'auto',
    defaultVisibleWeb: true, defaultVisibleMobile: true,
  },
  {
    key: 'kpi_active_vehicles',
    titleKey: 'dashboard.kpi_active_vehicles',
    descriptionKey: 'dashboard.kpi_active_vehicles_desc',
    component: ActiveVehiclesWidget,
    minColSpan: 1, maxColSpan: 1,
    supportsHeightChoice: false,
    defaultColSpan: 1, defaultHeight: 'auto',
    defaultVisibleWeb: true, defaultVisibleMobile: false,
  },
  {
    key: 'kpi_utilization',
    titleKey: 'dashboard.kpi_utilization',
    descriptionKey: 'dashboard.kpi_utilization_desc',
    component: UtilizationWidget,
    minColSpan: 1, maxColSpan: 1,
    supportsHeightChoice: false,
    defaultColSpan: 1, defaultHeight: 'auto',
    defaultVisibleWeb: true, defaultVisibleMobile: false,
  },
  {
    key: 'kpi_car_years',
    titleKey: 'dashboard.kpi_car_years',
    descriptionKey: 'dashboard.kpi_car_years_desc',
    component: CarYearsWidget,
    minColSpan: 1, maxColSpan: 1,
    supportsHeightChoice: false,
    defaultColSpan: 1, defaultHeight: 'auto',
    defaultVisibleWeb: true, defaultVisibleMobile: false,
  },
  // Hovedindhold
  {
    key: 'revenue_stats',
    titleKey: 'dashboard.widget_revenue_stats_title',
    descriptionKey: 'dashboard.widget_revenue_stats_desc',
    component: RevenueStatsWidget,
    minColSpan: 2, maxColSpan: 4,
    supportsHeightChoice: false,
    defaultColSpan: 3, defaultHeight: 'auto',
    defaultVisibleWeb: true, defaultVisibleMobile: false,
  },
  {
    key: 'upcoming_returns',
    titleKey: 'dashboard.upcoming_returns_title',
    descriptionKey: 'dashboard.upcoming_returns_desc',
    component: UpcomingReturnsWidget,
    minColSpan: 1, maxColSpan: 2,
    supportsHeightChoice: false,
    defaultColSpan: 1, defaultHeight: 'auto',
    defaultVisibleWeb: true, defaultVisibleMobile: true,
  },
  {
    key: 'ongoing_rentals',
    titleKey: 'dashboard.ongoing_rentals_title',
    descriptionKey: 'dashboard.ongoing_rentals_desc',
    component: OngoingRentalsWidget,
    minColSpan: 1, maxColSpan: 2,
    supportsHeightChoice: false,
    defaultColSpan: 1, defaultHeight: 'auto',
    defaultVisibleWeb: true, defaultVisibleMobile: true,
  },
  {
    key: 'upcoming_bookings',
    titleKey: 'dashboard.widget_upcoming_title',
    descriptionKey: 'dashboard.widget_upcoming_desc',
    component: UpcomingBookingsWidget,
    minColSpan: 1, maxColSpan: 2,
    supportsHeightChoice: false,
    defaultColSpan: 1, defaultHeight: 'auto',
    defaultVisibleWeb: true, defaultVisibleMobile: true,
  },
  {
    key: 'upcoming_service',
    titleKey: 'dashboard.upcoming_service_title',
    descriptionKey: 'dashboard.upcoming_service_desc',
    component: UpcomingServiceWidget,
    minColSpan: 1, maxColSpan: 2,
    supportsHeightChoice: false,
    defaultColSpan: 1, defaultHeight: 'auto',
    defaultVisibleWeb: true, defaultVisibleMobile: true,
  },
];

/** Bygger default-layout for alle widgets i registret. */
export function buildDefaultLayout(): LayoutItem[] {
  return WIDGET_REGISTRY.map((def, idx) => ({
    widget_key: def.key,
    position: idx,
    col_span: def.defaultColSpan,
    height: def.defaultHeight,
    visible_web: def.defaultVisibleWeb,
    visible_mobile: def.defaultVisibleMobile,
  }));
}

/**
 * Slår gemt layout og registret sammen.
 * - Bevarede widgets beholder brugerens indstillinger (i gemt rækkefølge).
 * - Nye widgets (ikke i det gemte layout) tilføjes sidst med defaults.
 * - Widgets der er fjernet fra registret droppes.
 */
export function mergeLayout(saved: LayoutItem[]): LayoutItem[] {
  const knownKeys = new Set(WIDGET_REGISTRY.map(d => d.key));
  const result: LayoutItem[] = saved.filter(i => knownKeys.has(i.widget_key));

  const savedKeys = new Set(result.map(i => i.widget_key));
  for (const def of WIDGET_REGISTRY) {
    if (!savedKeys.has(def.key)) {
      result.push({
        widget_key: def.key,
        position: result.length,
        col_span: def.defaultColSpan,
        height: def.defaultHeight,
        visible_web: def.defaultVisibleWeb,
        visible_mobile: def.defaultVisibleMobile,
      });
    }
  }

  return result.map((item, idx) => ({ ...item, position: idx }));
}
